import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { imageEditingActions, Template } from '../../reducers/imageEditing';
import Modal from '../common/Modal';
import Button from '../common/Button';

interface TemplateOverwriteModalProps {
  show: boolean;
  onHide: () => void;
}

const TemplateOverwriteModal: React.FC<TemplateOverwriteModalProps> = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState('');
  const [nameError, setNameError] = useState('');
  const [mode, setMode] = useState<'select' | 'naming'>('select');

  const { features, templateLoading, templates } = useSelector((state: RootState) => state.imageEditing);

  // モーダルが開かれたときにリセット
  useEffect(() => {
    if (show) {
      setSelectedTemplateId('');
      setTemplateName('');
      setNameError('');
      setMode('select');
    }
  }, [show]);

  // 選択されたテンプレートの情報を取得
  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  // 現在の設定を取得
  const getCurrentSettings = () => {
    const settings: any = {
      compression: features.compression.enabled ? features.compression.settings : null,
      watermark: features.watermark.enabled ? features.watermark.settings : null,
    };

    // 個別処理モードの設定
    if (features.contrastAdjustment?.enabled) {
      settings.contrastAdjustment = features.contrastAdjustment.settings;
    }
    if (features.brightnessAdjustment?.enabled) {
      settings.brightnessAdjustment = features.brightnessAdjustment.settings;
    }
    if (features.whiteBalance?.enabled) {
      settings.whiteBalance = features.whiteBalance.settings;
    }

    return settings;
  };

  // 名前のバリデーション
  const validateName = (name: string): boolean => {
    if (!name.trim()) {
      setNameError('テンプレート名を入力してください');
      return false;
    }
    if (name.trim().length > 20) {
      setNameError('テンプレート名は20文字以内で入力してください');
      return false;
    }
    // 重複チェック（選択されたテンプレート以外）
    if (templates.some(t => t.id !== selectedTemplateId && t.name === name.trim())) {
      setNameError('同じ名前のテンプレートが既に存在します');
      return false;
    }
    setNameError('');
    return true;
  };

  // 次へボタンの処理
  const handleNext = () => {
    if (selectedTemplate) {
      setTemplateName(selectedTemplate.name);
      setMode('naming');
    }
  };

  // 戻るボタンの処理
  const handleBack = () => {
    setMode('select');
    setNameError('');
  };

  // 保存処理
  const handleSave = async () => {
    if (!validateName(templateName)) {
      return;
    }

    const settings = getCurrentSettings();

    await dispatch(imageEditingActions.updateTemplate({
      id: selectedTemplateId,
      name: templateName.trim(),
      settings
    }) as any);

    onHide();
  };

  // 日付フォーマット
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 文字数カウント
  const charCount = templateName.length;
  const charCountColor = charCount > 20 ? 'text-red-500' : charCount > 15 ? 'text-yellow-500' : 'text-gray-500';

  // モード別のアクション
  const selectModeActions = (
    <>
      <Button
        onClick={handleNext}
        variant="primary"
        disabled={!selectedTemplateId || templateLoading}
      >
        次へ
      </Button>
      <Button variant="secondary" onClick={onHide} disabled={templateLoading}>
        キャンセル
      </Button>
    </>
  );

  const namingModeActions = (
    <>
      <Button
        onClick={handleSave}
        variant="primary"
        disabled={templateLoading || !templateName.trim() || !!nameError}
      >
        {templateLoading ? '保存中...' : '上書き保存'}
      </Button>
      <Button variant="secondary" onClick={handleBack} disabled={templateLoading}>
        戻る
      </Button>
    </>
  );

  return (
    <Modal
      show={show}
      onClose={onHide}
      title={mode === 'select' ? 'テンプレートを選択' : 'テンプレート名を変更'}
      size="lg"
      actions={mode === 'select' ? selectModeActions : namingModeActions}
    >
      {mode === 'select' ? (
        <>
          <div className="mt-2 mb-4">
            <p className="text-sm text-gray-600">
              保存できるテンプレート数が上限（5個）に達しています。
              <br />
              上書きするテンプレートを選択してください。
            </p>
          </div>
          <div className="space-y-2">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => setSelectedTemplateId(template.id)}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedTemplateId === template.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-gray-900">{template.name}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      最終更新: {formatDate(template.modified)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {Object.keys(template.settings).filter(k => template.settings[k as keyof typeof template.settings]).length}個の設定
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="mt-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>{selectedTemplate?.name}</strong> を上書きします
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              新しいテンプレート名
            </label>
            <input
              type="text"
              className={`w-full px-3 py-2 border ${nameError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
              placeholder="テンプレート名を入力（最大20文字）"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                validateName(e.target.value);
              }}
              maxLength={25}
            />
            <div className="flex justify-between mt-1">
              {nameError && (
                <span className="text-sm text-red-600 font-medium">{nameError}</span>
              )}
              <span className={`text-sm ml-auto ${charCountColor}`}>
                {charCount}/20
              </span>
            </div>
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600 mb-2">保存される設定:</p>
            <ul className="text-sm text-gray-700 space-y-1">
              {features.compression.enabled && <li>• 圧縮設定</li>}
              {features.watermark.enabled && <li>• ウォーターマーク設定</li>}
              {features.contrastAdjustment?.enabled && <li>• コントラスト調整</li>}
              {features.brightnessAdjustment?.enabled && <li>• 明るさ調整</li>}
              {features.whiteBalance?.enabled && <li>• ホワイトバランス</li>}
            </ul>
          </div>
        </>
      )}
    </Modal>
  );
};

export default TemplateOverwriteModal;